import { ProjectMemberPicker } from '@/components/projects/ProjectMemberPicker'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { getErrorMessage } from '@/api/errors'
import { useAssignWorkItem } from '@/hooks'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { WorkItem } from '@/types'
import { Check, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface AssignWorkItemDialogProps {
  projectId: string
  workItem: WorkItem
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AssignWorkItemDialog({ projectId, workItem, open, onOpenChange }: AssignWorkItemDialogProps) {
  const assignWorkItem = useAssignWorkItem(projectId)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(workItem.assignedToUserId)

  const hasChanged = selectedUserId !== workItem.assignedToUserId

  const handleAssign = async () => {
    try {
      await assignWorkItem.mutateAsync({ id: workItem.id, data: { assignedToUserId: selectedUserId } })
      toast.success(selectedUserId ? 'Work item assigned successfully' : 'Work item unassigned successfully')
      onOpenChange(false)
    } catch (error) {
      toast.error(
        extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to assign work item',
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Assign User</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="sr-only">
          Choose a project member to assign to this work item.
        </DialogDescription>
        <div className="space-y-2">
          <Label htmlFor="assign-work-item-member" className="sr-only">Assign to</Label>
          <ProjectMemberPicker
            projectId={projectId}
            inputId="assign-work-item-member"
            value={selectedUserId}
            onChange={setSelectedUserId}
            valueLabel={workItem.assignedToDisplayName || workItem.assignedToEmail}
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={assignWorkItem.isPending || !hasChanged}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            <Check className="h-5 w-5" />
            {assignWorkItem.isPending ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
